
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Button,
    CardContent,
    CardActions,
    Divider,
    Grid,
    Tab,
    Tabs,
    Typography
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import useConfig from 'hooks/useConfig';
import { gridSpacing } from 'store/constant';
import VenueDetails from './VenueDetails';
import Turf from './Turf';
import Price from './Price';
import AccountDetails from './AccountDetails';
import Settings from './Settings';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';
import MasterService from 'services/MasterService';
// import { Manager } from './Manager';

// assets
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
// import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';

// tab content customize
function TabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && <div>{children}</div>}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

// tabs option
const tabsOption = [
    { label: 'Details', icon: <StorefrontIcon />, caption: 'Venue Info' },
    { label: 'Court', icon: <CorporateFareIcon />, caption: 'Add & Update Court' },
    { label: 'Price Configuration', icon: <CurrencyRupeeIcon />, caption: 'Update Price' },
    { label: 'Account Details', icon: <AccountBalanceIcon />, caption: 'Add Account Details' },
    { label: 'Settings', icon: <SettingsIcon />, caption: 'Venue Settings' }
    // { label: 'Manager', icon: <ManageAccountsIcon />, caption: 'Manager Details' }
];

export default function CreateVenue() {

    const theme = useTheme();
    const { venueUuid } = useParams();
    const location = useLocation();
    const { pathname } = location;
    const parts = pathname.split('/');
    const actionType = parts[parts.length - 1];
    const { borderRadius } = useConfig();
    const [activeTab, setActiveTab] = React.useState(0);
    const [venueDetails, setVenueDetails] = useState(null);
    const [reload, setReload] = useState(false);
    const [sportsTypes, setSportsType] = useState([])

    const handleChange = (event, newValue) => {
        if (actionType != 'create') {
            setActiveTab(newValue);
        }
    };

    const getVenueHandler = (res) => {
        setVenueDetails(res.data);
    };

    const getStHandler = (response) => {
        const data = response.data;
        setSportsType(data);
    };

    const getVenueApi = useApi(VenueService.getVenueByUuid, getVenueHandler);
    const getSportsTypeListApi = useApi(MasterService.getDropdownSportsType, getStHandler);

    useEffect(() => {
        if (venueUuid) {
            getVenueApi.execute(venueUuid);
        }
    }, [reload]);

    useEffect(() => {
        getSportsTypeListApi.execute();
    }, [reload]);

    return (
        <MainCard title="Venue Onboard">
            <Grid container spacing={gridSpacing}>
                <Grid item xs={12} lg={3}>
                    <Tabs
                        value={activeTab}
                        orientation="vertical"
                        variant="scrollable"
                        onChange={handleChange}
                        textColor="secondary"
                        indicatorColor="secondary"
                        sx={{
                            '& .MuiTabs-flexContainer': {
                                borderBottom: 'none'
                            },
                            '& button': {
                                color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
                                minHeight: 'auto',
                                minWidth: '100%',
                                py: 1.5,
                                px: 2,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                textAlign: 'left',
                                justifyContent: 'flex-start',
                                borderRadius: `${borderRadius}px`
                            },
                            '& button.Mui-selected': {
                                color: theme.palette.secondary[800],
                                background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100]
                            },
                            '& button > svg': {
                                marginBottom: '0px !important',
                                marginRight: 1.25,
                                marginTop: 1.25,
                                height: 20,
                                width: 20
                            },
                            '& button > div > span': {
                                display: 'block'
                            },
                            '& > div > span': {
                                display: 'none'
                            }
                        }}
                    >
                        {tabsOption.map((tab, index) => (
                            <Tab
                                icon={tab.icon}
                                key={index}
                                label={
                                    <Grid container direction="column">
                                        <Typography variant="subtitle1" color="inherit">
                                            {tab.label}
                                        </Typography>
                                        <Typography component="div" variant="caption" sx={{ textTransform: 'capitalize' }}>
                                            {tab.caption}
                                        </Typography>
                                    </Grid>
                                }
                                {...a11yProps(index)}
                            />
                        ))}
                    </Tabs>
                </Grid>
                <Grid item xs={12} lg={9}>
                    <CardContent
                        sx={{
                            borderLeft: '1px solid',
                            borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[200],
                            height: '100%',
                            paddingTop: '0px'
                        }}
                    >
                        <TabPanel value={activeTab} index={0}>
                            <VenueDetails
                                actionType={actionType}
                                venueUuid={venueUuid}
                                venueDetails={venueDetails}
                                setDetailsReload={setReload}
                                handleChange={handleChange}
                                sportsTypes={sportsTypes}
                            />
                        </TabPanel>
                        <TabPanel value={activeTab} index={1}>
                            <Turf
                                venueUuid={venueUuid}
                                venueDetails={venueDetails}
                                sportsTypes={sportsTypes}
                                setSportsType={setSportsType}
                            />
                        </TabPanel>
                        <TabPanel value={activeTab} index={2}>
                            <Price
                                venueUuid={venueUuid}
                                sportsTypes={sportsTypes}
                            />
                        </TabPanel>
                        <TabPanel value={activeTab} index={3}>
                            <AccountDetails
                                venueUuid={venueUuid}
                            />
                        </TabPanel>
                        <TabPanel value={activeTab} index={4}>
                            <Settings
                                venueUuid={venueUuid}
                                venueDetails={venueDetails}
                                setDetailsReload={setReload}
                            />
                        </TabPanel>
                        {/* <TabPanel value={activeTab} index={5}>
                            <Manager venueUuid={venueUuid} />
                        </TabPanel> */}
                    </CardContent>
                </Grid>
            </Grid>
            <Divider />
            <CardActions>
                <Grid container justifyContent="space-between" spacing={0}>
                    <Grid item>
                        {activeTab > 0 && (
                            <Button color="primary" variant="outlined" size="medium" onClick={(e) => handleChange(e, activeTab - 1)}>
                                Back
                            </Button>
                        )}
                    </Grid>
                    <Grid item>
                        {activeTab < tabsOption.length - 1 && (
                            <Button color="primary" variant="outlined" size="medium" onClick={(e) => handleChange(e, 1 + activeTab)}>
                                Next
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </CardActions>
        </MainCard>
    );
};

